import {
  findLinkedTaskType,
  parseFlowTemplate,
} from '@/etc/flowSpecRoles'

export const AUDIO_MODE_INPUT_KEY = '__vyby_audio_mode'
export const AUDIO_GENERATION_INPUT_KEY = '__vyby_generate_audio'
export const DEFAULT_AUDIO_MODE = 'enabled'

export const AUDIO_MODE_OPTIONS = Object.freeze([
  { value: 'enabled', label: 'Audio enabled', modifier: '' },
  { value: 'no_music', label: 'No music', modifier: '-no music' },
  { value: 'no_voice', label: 'No voice', modifier: '-no voice' },
  { value: 'no_audio', label: 'No audio', modifier: '-no audio' },
])

const AUDIO_MODES = new Set(AUDIO_MODE_OPTIONS.map((option) => option.value))
const PROMPT_AUDIO_MODEL_IDS = new Set(['veo3', 'veo3.1', 'veo3.1_fast'])

function compactString(value) {
  return `${value || ''}`.trim()
}

function firstLinkedProviderField(input) {
  const links = input?.links && typeof input.links === 'object' ? input.links : {}
  const link = Object.values(links).find((candidate) => candidate?.input)
  return compactString(link?.input)
}

function resolveSelectedModel(flow, taskType, taskSpec) {
  const modelField =
    compactString(taskSpec?._meta?.roles?.model) ||
    ['model', 'model_name', 'model_id'].find((key) => taskSpec?._meta?.specification?.[key]) ||
    ''
  if (!modelField) return ''

  const inputs = flow?.inputs && typeof flow.inputs === 'object' ? flow.inputs : {}
  const linked = Object.values(inputs).find(
    (input) => firstLinkedProviderField(input) === modelField,
  )
  const manual = compactString(linked?.manual)
  if (manual) return manual

  return (
    compactString(taskSpec?.inputs?.[modelField]) ||
    compactString(taskSpec?._meta?.specification?.[modelField]?.default)
  )
}

function declaredAudioSupport(meta, model) {
  const declared = meta?.audioGeneration ?? meta?.capabilities?.audioGeneration
  if (typeof declared === 'boolean') return declared
  if (Array.isArray(declared)) return !!model && declared.includes(model)
  if (!declared || typeof declared !== 'object') return null
  if (declared.enabled === false) return false
  const models = Array.isArray(declared.models) ? declared.models : []
  if (models.length) return !!model && models.includes(model)
  return declared.enabled === true ? true : null
}

export function normalizeAudioMode(value) {
  const mode = compactString(value).toLowerCase()
  return AUDIO_MODES.has(mode) ? mode : DEFAULT_AUDIO_MODE
}

export function audioModeOption(value) {
  const mode = normalizeAudioMode(value)
  return AUDIO_MODE_OPTIONS.find((option) => option.value === mode) || AUDIO_MODE_OPTIONS[0]
}

export function audioPromptModifier(value) {
  return audioModeOption(value).modifier
}

export function appendAudioPromptModifier(prompt, value) {
  const text = compactString(prompt)
  const modifier = audioPromptModifier(value)
  if (!text || !modifier) return text

  const escaped = modifier.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  if (new RegExp(`(?:^|\\s)${escaped}(?=\\s|[.,;:!?]|$)`, 'i').test(text)) return text
  return `${text} ${modifier}`
}

export function resolveAudioGenerationCapability(flow) {
  const taskType = findLinkedTaskType(flow)
  const template = parseFlowTemplate(flow)
  const taskSpec = taskType ? template?.[taskType] : null
  const meta = taskSpec?._meta || {}
  const specification =
    meta.specification && typeof meta.specification === 'object'
      ? meta.specification
      : {}
  const model = resolveSelectedModel(flow, taskType, taskSpec)
  const provider = compactString(flow?.api_name || flow?.provider).toLowerCase()
  const family = compactString(meta.family).toLowerCase()
  const providerField =
    ['generate_audio', 'audio'].find(
      (key) => specification[key]?.type === 'boolean',
    ) || ''

  const declared = declaredAudioSupport(meta, model)
  const supported =
    declared ??
    (!!providerField ||
      family === 'seedance' ||
      PROMPT_AUDIO_MODEL_IDS.has(model))

  return {
    supported: !!supported,
    taskType,
    provider,
    family,
    model,
    providerField,
  }
}
